import React, { Component } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { reduxForm, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { Redirect } from 'react-router-dom';
import { store } from 'store';
import { banner } from 'actions';
import { Button, Datepicker, Input, Select } from 'common/components';
import {
  graphQlToFormErrors,
  omitDeep,
  generateUUID,
  capitalizeString,
} from 'common/utils';
import {
  required,
  numberOnly,
  maxLength,
  maxValue,
  minValue,
} from 'common/form-validators';
import { limitDigits, onlyNumbers, trim } from 'common/normalizers';
import { toCommaNumber } from 'common/formatters';
import mixpanel from 'mixpanel-browser';

import {
  MUTATION_CREATE_CONTRACT,
  MUTATION_UPDATE_CONTRACT,
  FETCH_CONTRACT,
  FETCH_ALL_CONTRACTS,
} from 'api/contract';
import {
  ANALYTICS,
  BANNER_IDS,
  CONTRACT_SIZE,
  CROP_SIZE,
  ERROR_DEFAULT,
  OPTIONS,
} from 'common/constants';

export class ContractForm extends Component {
  constructor(props) {
    super(props);

    // validations
    this.nameMax = value => maxLength(value, CONTRACT_SIZE.MAX_TITLE);
    this.varietyMax = value => maxLength(value, CROP_SIZE.MAX_TITLE);
    this.notesMax = value => maxLength(value, CONTRACT_SIZE.MAX_NOTES);
    this.binSizeMin = value => minValue(value, CONTRACT_SIZE.MIN);
    this.binSizeMax = value =>
      maxValue(value, CONTRACT_SIZE.MAX_BIN)
        ? `Bin Capacity should be between 0 and ${toCommaNumber(
            CONTRACT_SIZE.MAX_BIN
          )} Bushels`
        : undefined;

    // normlizers
    this.normalizeCapacity = value => limitDigits(onlyNumbers(value), 7, 2);
  }

  static displayName = 'ContractForm';

  static propTypes = {
    // manually added
    closeForm: PropTypes.func.isRequired,
    id: PropTypes.string,
    season: PropTypes.number.isRequired,

    // reduxform
    error: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
    valid: PropTypes.bool.isRequired,
    pristine: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,
    // from graphql
    initialValues: PropTypes.object,
    // from dispatch
    onSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    contract: PropTypes.any,
  };

  static defaultProps = {
    closeForm: () => null,
  };

  render() {
    const {
      season,
      loading,
      contract,
      onSubmit,
      closeForm,
      error,
      handleSubmit,
      valid,
      pristine,
      submitting,
    } = this.props;
    if (!loading && contract === null) return <Redirect to="/error" />;
    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <small className={'text--info'}>* Required</small>
        <h2 className={'text--header'}>Buyer</h2>
        <Input
          name="company"
          disabled={submitting}
          title="Company"
          validate={[required, this.nameMax]}
          normalizeOnBlur={trim}
        />

        <Input
          name="contractNumber"
          disabled={submitting}
          title="Contract Number"
          normalizeOnBlur={trim}
        />

        <Datepicker
          name="contractDate"
          disabled={submitting}
          title="Contract Date"
          season={season}
        />

        <Datepicker
          name="deliveryDate"
          disabled={submitting}
          title="Delivery Date"
          season={season}
          validate={[required]}
        />

        <div className={'field__group'}>
          <h2 className={'text--header'}>Crop</h2>
          <Select
            name="crop"
            disabled={submitting}
            title="Crop"
            options={OPTIONS.CROP_OPTIONS}
            validate={[required]}
          />

          <Input
            name="variety"
            disabled={submitting}
            title="Variety"
            normalize={capitalizeString}
            normalizeOnBlur={trim}
            validate={[this.varietyMax]}
          />

          <Input
            name="basePrice"
            disabled={submitting}
            title="Base Price"
            infoText="$/Bushels"
            validate={[required, numberOnly]}
            normalize={this.normalizeCapacity}
            format={toCommaNumber}
          />

          <Input
            name="quantity"
            disabled={submitting}
            title="Total Quantity"
            infoText="Bushels"
            validate={[required, numberOnly, this.binSizeMin, this.binSizeMax]}
            normalize={this.normalizeCapacity}
            format={toCommaNumber}
          />

          <Input
            name="delivered"
            disabled={submitting}
            title="Delivered Quantity"
            infoText="Bushels"
            validate={[numberOnly, this.binSizeMax]}
            normalize={this.normalizeCapacity}
            format={toCommaNumber}
          />

          <Input
            name="notes"
            disabled={submitting}
            title="Notes"
            validate={[this.notesMax]}
            normalizeOnBlur={trim}
          />
          <small>
            Add notes on grain quality, movement / storage instructions, etc.
          </small>
        </div>

        {error && <p className="text--error">{error}</p>}

        <div className={'field__group'}>
          <fieldset className={'field text--center'}>
            <Button
              type="submit"
              className="btn btn--primary gap-top-small"
              disabled={!valid || pristine || submitting}
            >
              Save
            </Button>
          </fieldset>

          <fieldset className={'field text--center'}>
            <Button
              type="button"
              className="btn"
              onClick={closeForm}
              disabled={submitting}
            >
              Cancel
            </Button>
          </fieldset>
        </div>
      </form>
    );
  }
}

const formSettings = { form: 'contractform', enableReinitialize: true };
// const field = formValueSelector(formSettings.form);

const buildContractSchemaForOptimisticResponse = payload => {
  const { basePrice, crop, quantity } = payload;
  payload.__typename = 'Contract';
  if (basePrice) {
    basePrice.__typename = 'Currency';
  }
  if (crop) {
    crop.__typename = 'CropIdentifier';
  }
  if (quantity) {
    quantity.__typename = 'ContractQuantity';
    const { contracted, delivered, pending } = quantity;
    if (contracted) {
      contracted.__typename = 'Amount';
    }
    if (delivered) {
      delivered.__typename = 'Amount';
    }
    if (pending) {
      pending.__typename = 'Amount';
    } else {
      const pendingSize = contracted.size - delivered.size;
      payload.quantity = {
        ...quantity,
        pending: {
          __typename: 'Amount',
          size: pendingSize,
          type: 'bushels',
        },
      };
    }
  }
  payload.active = true;
  return payload;
};

const mapState = ({ settings: { season } }) => ({ season });

const mapDispatch = (dispatch, props) => ({
  onSubmit: async values => {
    let vars = {
      basePrice: {
        currency: 'CAD',
        value: parseFloat(values.basePrice),
      },
      crop: {
        name: values.crop,
        variety: values.variety || null,
      },
      quantity: {
        contracted: {
          size: values.quantity,
          type: 'bushels',
        },
        delivered: {
          size: values.delivered ? values.delivered : 0,
          type: 'bushels',
        },
      },
      notes: values.notes ? values.notes : null,
      season: store.getState().settings.season,
      deliveryDate:
        typeof values.deliveryDate === 'object'
          ? values.deliveryDate.toISOString()
          : values.deliveryDate,
    };

    if (values.contractDate) {
      vars = {
        contractDate:
          typeof values.contractDate === 'object'
            ? values.contractDate.toISOString()
            : values.contractDate,
        ...vars,
      };
    }
    // get new id only when the storage name is updated
    const initialName = props.initialValues && props.initialValues.company;
    if (props.id === 'add' || initialName !== values.company) {
      vars = {
        company: values.company,
        ...vars,
      };
    }

    if (values.contractNumber) {
      vars = {
        contractNumber: values.contractNumber,
        ...vars,
      };
    }

    const ERROR_AMOUNT =
      'Delivered quantity can not be more than the total contracted quantity, please try again';

    // FE quantity validation happens
    if (minValue(values.quantity, values.delivered)) {
      throw new SubmissionError({ _error: ERROR_AMOUNT });
      // BE validation happens
    } else {
      const res =
        props.id === 'add'
          ? await props.onCreateContract(vars)
          : await props.onUpdateContract(props.id, vars);

      if (
        res &&
        res.data &&
        (res.data.createContract || res.data.updateContract)
      ) {
        dispatch(
          banner.display(
            BANNER_IDS.APP,
            `CONTRACT ${props.id === 'add' ? 'ADDED' : 'UPDATED'}`,
            false
          )
        );
        props.closeForm();
      } else if (res && res.errors) {
        const _error = res.errors[0] ? res.errors[0].message : ERROR_DEFAULT;
        throw new SubmissionError({ _error });
      } else {
        throw new SubmissionError({ _error: ERROR_DEFAULT });
      }
    }
  },
});

export default compose(
  graphql(FETCH_CONTRACT, {
    options: props => ({
      variables: { id: props.id },
      fetchPolicy: 'cache-first',
    }),
    props: props => {
      if (!props.data || !props.data.getContract) {
        return {
          loading: props.data.loading,
          contract: props.data.getContract,
        };
      }
      const contract = props.data.getContract;
      return {
        initialValues: {
          company: contract.company,
          contractNumber: contract.contractNumber,
          contractDate: contract.contractDate,
          deliveryDate: contract.deliveryDate,
          crop: contract.crop.name,
          variety: contract.crop.variety,
          basePrice:
            contract.basePrice.value && contract.basePrice.value.toFixed(2),
          quantity: contract.quantity.contracted.size,
          delivered: contract.quantity.delivered.size,
          notes: contract.notes,
        },
        ...props,
      };
    },
  }),

  graphql(MUTATION_CREATE_CONTRACT, {
    options: () => ({
      onCompleted: ({ createContract }) => {
        if (createContract) {
          mixpanel.track(ANALYTICS.EVENTS.CONTRACT.ADDED, {
            [ANALYTICS.PROPERTIES.CONTRACT.DUE_DATE]:
              createContract.deliveryDate,
            [ANALYTICS.PROPERTIES.CONTRACT.DATE]:
              createContract.contractDate || '',
          });
          mixpanel.people.increment(
            ANALYTICS.PROPERTIES.CONTRACT.CONTRACTS_ADDED
          );
        }

        return createContract;
      },
    }),
    props: props => ({
      onCreateContract: contractCreateInput => {
        const uuid = generateUUID();
        const expectedId = `CONTRACT:${contractCreateInput.season}:${uuid}`;

        const constructedContract = {
          id: expectedId,
          active: true,
          contractDate: null,
          contractNumber: null,
          notes: null,
          ...contractCreateInput,
        };
        contractCreateInput.uuid = uuid;
        return props
          .mutate({
            variables: {
              contractCreateInput: omitDeep(contractCreateInput, '__typename'),
            },
            optimisticResponse: () => ({
              createContract: buildContractSchemaForOptimisticResponse(
                constructedContract
              ),
            }),
            update: (proxy, { data: { createContract } }) => {
              const { season } = contractCreateInput;
              // Current version mockedProvider from react-apollo does not support mocked cache
              // TODO update react-apollo to mock cache and manually writeQuery in its corresponding test
              // and remove the following line
              if (process.env.NODE_ENV === 'test') return;
              if (season === store.getState().settings.season) {
                const proxyData = proxy.readQuery({
                  query: FETCH_ALL_CONTRACTS,
                  variables: { active: true, season },
                });
                createContract.id = expectedId;

                proxyData.getAllContracts.push(createContract);
                const cachedObj = {
                  query: FETCH_ALL_CONTRACTS,
                  variables: { active: true, season },
                  data: proxyData,
                };
                proxy.writeQuery(cachedObj);
              }
            },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  graphql(MUTATION_UPDATE_CONTRACT, {
    props: props => ({
      onUpdateContract: (id, contractUpdateInput) => {
        return props
          .mutate({
            variables: {
              id,
              contractUpdateInput: omitDeep(contractUpdateInput, '__typename'),
            },
            optimisticResponse: () => ({
              updateContract: buildContractSchemaForOptimisticResponse({
                id,
                ...contractUpdateInput,
              }),
            }),
            update: (proxy, { data: { updateContract } }) => {
              const proxyData = proxy.readQuery({
                query: FETCH_CONTRACT,
                variables: { id },
              });
              const updatedData = {
                ...proxyData.getContract,
                updateContract,
              };
              const cachedObj = {
                query: FETCH_CONTRACT,
                variables: { id },
                data: { getContract: updatedData },
              };
              proxy.writeQuery(cachedObj);
            },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  connect(
    mapState,
    mapDispatch
  ),
  reduxForm(formSettings)
)(ContractForm);



// WEBPACK FOOTER //
// ./src/containers/ContractForm/index.js